import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { statusLabels, statusTone, type Tone } from "@/lib/format";
import type { ShipmentStatus } from "@/lib/types";

export function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  loading?: boolean;
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const variants = {
    primary: "bg-brand text-white hover:bg-brand-strong",
    secondary: "border border-ink-300 bg-white text-ink-900 hover:border-ink-400 hover:bg-ink-50",
    ghost: "text-ink-700 hover:bg-ink-100",
    danger: "border border-signal-bad/30 bg-white text-signal-bad hover:bg-signal-bad/5",
  };
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "px-3 py-1.5 text-sm" : "px-4 py-2.5 text-[15px]",
        variants[variant],
        className,
      )}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cx("inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent", className)}
    />
  );
}

/**
 * The red star on a required field's label.
 *
 * Marked up so it is a mark, not a word: the asterisk is decorative and screen
 * readers get `aria-required` on the input instead, which they announce as part
 * of the field rather than as a stray character in its name.
 */
export function RequiredMark() {
  return (
    <span aria-hidden="true" className="ml-0.5 text-signal-bad">
      *
    </span>
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  hint?: string;
};

export function Field({ label, error, hint, className, id, required, ...rest }: FieldProps) {
  const inputId = id ?? rest.name ?? label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className={className}>
      <label className="ws-label" htmlFor={inputId}>
        {label}
        {required && <RequiredMark />}
      </label>
      <input
        {...rest}
        id={inputId}
        // aria-required, not the HTML `required` attribute: the browser's own
        // validation bubble would pre-empt ours, which says more and puts the
        // cursor in the right place.
        aria-required={required ? true : undefined}
        aria-invalid={error ? true : undefined}
        className={cx("ws-input", error && "border-signal-bad focus:border-signal-bad focus:ring-signal-bad/20")}
      />
      {hint && !error && <p className="ws-hint">{hint}</p>}
      {error && <p className="ws-error">{error}</p>}
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

/** A labelled run of options, rendered as an optgroup. */
export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string;
  /** Loose options, shown before any groups. */
  options?: SelectOption[];
  /**
   * Headed sections. A long list that splits into "what we carry" and "what we
   * do not" is unreadable as one run, and a browser renders an optgroup heading
   * unselectably and in bold on desktop, on Android, and in iOS's wheel.
   */
  groups?: SelectGroup[];
};

export function Select({ label, error, options = [], groups = [], className, id, required, ...rest }: SelectProps) {
  const selectId = id ?? rest.name ?? label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className={className}>
      <label className="ws-label" htmlFor={selectId}>
        {label}
        {required && <RequiredMark />}
      </label>
      <select
        {...rest}
        id={selectId}
        aria-required={required ? true : undefined}
        aria-invalid={error ? true : undefined}
        className={cx("ws-input", error && "border-signal-bad")}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
        {groups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {error && <p className="ws-error">{error}</p>}
    </div>
  );
}

const toneClasses: Record<Tone, string> = {
  neutral: "bg-ink-100 text-ink-600",
  attention: "bg-brand-soft text-brand-strong",
  progress: "bg-brand-soft text-brand",
  good: "bg-signal-good/10 text-signal-good",
  warn: "bg-signal-warn/10 text-signal-warn",
  bad: "bg-signal-bad/10 text-signal-bad",
};

export function StatusBadge({ status }: { status: ShipmentStatus }) {
  return (
    <span className={cx("inline-flex rounded-lg px-2 py-1 text-xs font-medium", toneClasses[statusTone[status]])}>
      {statusLabels[status]}
    </span>
  );
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: Tone }) {
  return <span className={cx("inline-flex rounded-lg px-2 py-0.5 text-xs font-medium", toneClasses[tone])}>{children}</span>;
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("ws-card p-5", className)}>{children}</div>;
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-400">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="ws-card flex flex-col items-center px-6 py-14 text-center">
      <h3 className="text-base font-medium text-ink-900">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-ink-400">{body}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Alert({ children, tone = "bad" }: { children: ReactNode; tone?: Tone }) {
  return (
    <div className={cx("rounded-xl px-4 py-3 text-sm", toneClasses[tone])} role="alert">
      {children}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("animate-pulse rounded-lg bg-ink-200/60", className)} />;
}

/** Stat is the dashboard tile used across the merchant, ops and platform views. */
export function Stat({
  label,
  value,
  hint,
  tone = "neutral",
  loading = false,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: Tone;
  loading?: boolean;
}) {
  const accentText =
    tone === "bad" ? "text-signal-bad" : tone === "good" ? "text-signal-good" : "text-ink-900";
  return (
    <div className="ws-card p-5">
      <p className="text-sm text-ink-500">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-16" />
      ) : (
        <p className={cx("mt-1 text-3xl font-bold tracking-tight", accentText)}>{value}</p>
      )}
      {hint && <p className="mt-1 text-xs text-ink-400">{hint}</p>}
    </div>
  );
}

/** Table wraps the shared chrome so every data grid in the portal matches. */
export function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="ws-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-ink-200 bg-ink-50">
          <tr>
            {head.map((label) => (
              <th key={label} className="ws-th">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">{children}</tbody>
      </table>
    </div>
  );
}

/** Pagination is identical everywhere, so it is written once. */
export function Pager({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number;
  limit: number;
  offset: number;
  onChange: (next: number) => void;
}) {
  if (total <= limit) return null;
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-ink-500">
      <span>
        {offset + 1} to {Math.min(offset + limit, total)} of {total}
      </span>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          Previous
        </Button>
        <Button size="sm" variant="secondary" disabled={offset + limit >= total} onClick={() => onChange(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}
